/**
 * clickgen-tracker.js
 *
 * Used for tracking link impressions and clicks in slideshares
 * This tracks impressions and clicks for both organic links and any custom
 * links overlaid on the player.
 */
import TrackingUtils from './tracking-utils';

(function(win) {

  // Dependencies.
  var ss = win.slideshare_object,
    utils = ss.utils,

    // Constants
    APP_NAME = 'leads',
    EVENTS = {
      SlideShareLinkImpressionEvent: "SlideShareLinkImpressionEvent",
      SlideShareLinkClickEvent: "SlideShareLinkClickEvent"
    },
    DATA_ATTRS = {
      SlideIndex: 'data-slideIndex',
      LinkIndex: 'data-linkIndex'
    };

  /**
   * Creates a new ClickGenTracker instance.
   * @constructor
   */
  function ClickGenTracker() {
    // Set up tracking utils
    this._trackingUtils = new TrackingUtils();
    this._appId = this._trackingUtils.getAppId(APP_NAME);
    this._eventBodyDefaults = {
      requestHeader: {
        pageKey: this._trackingUtils.getPageKey(),
        path: win.document.location.pathname
      }
    };

    // Set up an impressions queue to keep track of any slides fow which impression events
    // were not fired due to the meta.js (which is fetched asynchronously) not
    // being available yet. this queue is flushed before any new impression or
    // click event is fired for the same slideshare.
    //
    // meta.js is only loaded after:
    //   1. user lands on slideview and click next to go to slide 2. OR
    //   2. user lands on slideview and jumps to a later slide
    //   Note: Links on the first 3 slides are not rendered, so by the time the
    //   user gets to slide 4 by pressing next, meta.js is already loaded.
    //   However, if the user jumps to slide 4 from the start, meta.js is loaded
    //   after the jump and is available for the next slide. In this case, slide
    //   4 should be stored in the queue.
    //
    // This queue is flushed before any new event is fired and also when the
    // links metadata is registered.
    //
    this._impressionQueue = [];

    // Set up a cache for the link info from the last link impression event so
    // that we can avoid sending duplicates in mobile swipe player when the
    // user starts swiping but does not actually go to the next slide.
    // This also helps in cases where the same link shows up multiple times in
    // meta.js. Only one impression event will be sent.
    this.lastLinkImpressionInfo = {}
  }

  ClickGenTracker.prototype = /** @lends ClickGenTracker.prototype */ {

    constructor: ClickGenTracker,

    LINK_TYPE: {
      Organic: 'ORGANIC',
      OverlaidCTA: 'OVERLAID_CTA'
    },

    /**
     * This should be called to register the links metadata once meta.js has been
     * successfully loaded asynchronously
     * @param links
     */
    registerLinksMetadata: function(links) {
      this.linksMetadata = links;
      // Flush the impression queue to track any prior link impressions in this
      // slideshare
      this.flushImpressionQueue();
    },

    /**
     * Fires a SlideShareLinkImpressionEvent for a custom linkType.
     * @param {String} url Destination Url of this link
     * @param {LINK_TYPE} linkType Type of custom link
     * @param {Integer} slide Optional SlideNumber at which the link is present
     * @returns {String} impressionId for this impression event
     */
    trackLinkImpressionEvent: function(url, linkType, slide) {
      var tracker = this,
        impressionId,
        eventBodyExtras,
        eventBody,
        slideshowUrn = this._trackingUtils.getUrn('Slideshow', ss.slideshow.id),
        linkInfo;

      if (!url || !linkType) {
        // No destination url or linkType specified, nothing to track
        return;
      }

      linkInfo = {
        slideshowUrn: slideshowUrn,
        url: url,
        linkType: linkType
      };

      if (slide) {
        linkInfo.slideNumber = slide;
      }

      // Generate a unique impression id for this link
      impressionId = tracker._trackingUtils.generateTrackingID();

      eventBodyExtras = {
        slideShareGuestUrn: tracker._trackingUtils.getSlideShareGuest(),
        impressionId: impressionId,
        linkInfo: linkInfo
      };
      eventBody = $.extend({}, tracker._eventBodyDefaults, eventBodyExtras);

      tracker._trackingUtils.recordTrackingEvent(tracker._appId,
        EVENTS.SlideShareLinkImpressionEvent, eventBody);

      return impressionId;
    },

    /**
     * Fires a SlideShareLinkClickEvent for the clicked custom link. Use
     * trackOrganicLinkClickEvent for tracking ORGANIC linkType clicks.
     * @param {String} impressionId Impression Id corresponding to the
     * impression event
     */
    trackLinkClickEvent: function(impressionId) {
      if (!impressionId) {
        // There should be no click without a prior impression
        return;
      }

      var eventBodyExtras = {
        impressionId: impressionId
      };
      const eventBody = $.extend({}, this._eventBodyDefaults, eventBodyExtras);

      this._trackingUtils.recordTrackingEvent(this._appId,
        EVENTS.SlideShareLinkClickEvent, eventBody);
    },


    /**
     * Fires a SlideShareLinkClickEvent for the clicked target. It uses the
     * impression id from the links metadata so that it matches the corresponding
     * impression.
     * @param {Object} target the area element that was clicked
     * @param {Object} links metadata from the player controller
     */
    trackOrganicLinkClickEvent: function(target) {
      var slideIndex,
        linkIndex,
        linkMetadata,
        eventBodyExtras,
        eventBody;

      if (!this.linksMetadata) {
        // Cannot track SlideShareLinkClickEvent trackOutboundClick event was
        // received but links metadata is not loaded yet
        return;
      }

      // First flush impression queue using the newly loaded links metadata to
      // make sure previous impression events from the same slideshare are fired
      this.flushImpressionQueue();

      if (target) {
        // Get the slide index and link index from the target to look up
        // corresponding data in link metadata.
        slideIndex = target.getAttribute(DATA_ATTRS.SlideIndex);
        linkIndex = target.getAttribute(DATA_ATTRS.LinkIndex);

        if (!slideIndex || !linkIndex || !this.linksMetadata[slideIndex]
          || !this.linksMetadata[slideIndex].links[linkIndex]) {
          // Cannot track SlideShareLinkClickEvent. linkMetadata not found
          return;
        }

        linkMetadata = this.linksMetadata[slideIndex].links[linkIndex];

        eventBodyExtras = {
          // Use the same impression id used in the corresponding impression
          // event that was saved in the link metadata
          impressionId: linkMetadata.impressionId
        };
        eventBody = $.extend({}, this._eventBodyDefaults, eventBodyExtras);

        this._trackingUtils.recordTrackingEvent(this._appId,
          EVENTS.SlideShareLinkClickEvent, eventBody);
      }
    },

    /**
     * Fires SlideShareLinkImpressionEvent for each link in slide given the metadata
     * @param {Integer} slide
     * @param {Object} links metadata from player controller
     * @private
     */
    _trackOrganicLinkImpressionEvent: function(slide) {
      var tracker = this,
        linksOnSlide,
        impressionId,
        eventBodyExtras,
        eventBody,
        slideshowUrn = this._trackingUtils.getUrn('Slideshow', ss.slideshow.id),
        linkType = this.LINK_TYPE.Organic,
        linkInfo;

      if (!this.linksMetadata) {
        // Cannot track SlideShareLinkImpressionEvent. Links metadata is not loaded yet
        return;
      }

      linksOnSlide = this.linksMetadata[slide];
      if (linksOnSlide && linksOnSlide.links) {
        linksOnSlide = linksOnSlide.links;

        // Generate a new impression id and tracking event for each link on slide
        linksOnSlide.forEach( function(linkMetadata) {
          linkInfo = {
            slideshowUrn: slideshowUrn,
            url: linkMetadata.url,
            slideNumber: slide,
            displayRectangle: tracker._getLinkDisplayRectangle(linkMetadata.box),
            linkType: linkType
          };

          if (tracker._isDuplicateImpression(linkInfo)) {
            // This is a duplicate of the last impression event. Skip.
            return;
          }

          // Generate a unique impression id for this link
          impressionId = tracker._trackingUtils.generateTrackingID();

          // Add this impression id to the links metadata object so that the
          // same impression id can be used while firing the click event
          linkMetadata.impressionId = impressionId;

          eventBodyExtras = {
            slideShareGuestUrn: tracker._trackingUtils.getSlideShareGuest(),
            impressionId: impressionId,
            linkInfo: linkInfo
          };
          eventBody = $.extend({}, tracker._eventBodyDefaults, eventBodyExtras);

          tracker._trackingUtils.recordTrackingEvent(tracker._appId,
            EVENTS.SlideShareLinkImpressionEvent, eventBody);

          // Cache the link info from this event
          tracker.lastLinkImpressionInfo = linkInfo;
        });
      }
    },

    /**
     * Flushes the Impression Queue to fire all past impressions for the current
     * slideshare that were not fired due to links metadata (meta.js) not being
     * present.
     * @param {Object} links metadata from player controller
     */
    flushImpressionQueue: function() {
      var tracker = this;
      this._impressionQueue.forEach( function(slide) {
        tracker._trackOrganicLinkImpressionEvent(slide);
      });
      this._impressionQueue = [];
    },

    /**
     * Returns whether links are disabled for this slide
     *   1) Link override OR super user --> TRUE
     *   2) On the first three slides --> FALSE
     *   3) Every other slide --> TRUE
     * Same as 00-slideshare_slide.js areLinksEnabled
     * @param {Integer} slide
     * @returns {boolean}
     * @private
     */
    _areLinksEnabledOnSlide: function(slide) {
      if (this.renderAllLinks ||
        (typeof ss !== 'undefined' && ss.user.su)) {
        return true;
      } else if (!slide || slide <= 3) {
        return false;
      } else {
        return true;
      }
    },

    /**
     * Converts the link box from link metadata (meta.js) to DisplayRectangle
     * format.
     * @param linkBox
     * @returns {{top: Number, left: Number, size: {height: number, width: number}}}
     * @private
     */
    _getLinkDisplayRectangle: function(linkBox) {
      // The box in meta.js is represented as (left, bottom, right, top)
      // We calculate the display rectangle as (top, left, size)
      var left = parseInt((linkBox[0] || 0), 10),
        top    = parseInt((linkBox[3] || 0), 10),
        right  = parseInt((linkBox[2] || 0), 10),
        bottom = parseInt((linkBox[1] || 0), 10);

      return {
        top: top,
        left: left,
        size: {
          height: bottom - top, // pixel value of bottom is larger than top
          width:  right - left  // pixel value of right is larger than left
        }
      };
    },

    /**
     * Compares the cached link info from the last impression to the current
     * link info
     * @param {Object} currentLinkInfo Object to be tracked
     * @returns {boolean}
     * @private
     */
    _isDuplicateImpression: function(currentLinkInfo) {
      var lastLinkInfo = this.lastLinkImpressionInfo;
      return JSON.stringify(lastLinkInfo) === JSON.stringify(currentLinkInfo);
    }
  };

  utils.exports('tracking.ClickGenTracker', ClickGenTracker);

}(window));
